import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  clearStoredSession,
  fetchCurrentUser,
  revokeSession,
  fetchSessions,
  loginAccount,
  logoutAccount,
  readStoredSession,
  registerAccount,
  writeStoredSession,
} from '../lib/authClient';

const AuthContext = createContext(null);

function buildStoredSession(response) {
  return {
    accessToken: response.accessToken,
    user: response.user,
  };
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const stored = readStoredSession();
    if (!stored?.accessToken) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const restoreSession = async () => {
      try {
        const meResponse = await fetchCurrentUser(stored.accessToken);
        const sessionsResponse = await fetchSessions(stored.accessToken).catch(() => ({ sessions: [] }));

        if (cancelled) return;

        setToken(stored.accessToken);
        setUser(meResponse.user || null);
        setSessions(sessionsResponse.sessions || []);
        writeStoredSession({
          accessToken: stored.accessToken,
          user: meResponse.user || null,
        });
      } catch {
        clearStoredSession();
        if (cancelled) return;
        setToken(null);
        setUser(null);
        setSessions([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const applyAuthResponse = async (response) => {
    const nextSession = buildStoredSession(response);
    const nextToken = nextSession.accessToken;

    setToken(nextToken);
    setUser(response.user || null);
    writeStoredSession(nextSession);

    const sessionsResponse = await fetchSessions(nextToken).catch(() => ({ sessions: [] }));
    setSessions(sessionsResponse.sessions || []);

    return response.user || null;
  };

  const signIn = async (credentials) => {
    setIsSubmitting(true);
    try {
      const response = await loginAccount(credentials);
      return await applyAuthResponse(response);
    } finally {
      setIsSubmitting(false);
    }
  };

  const signUp = async (payload) => {
    setIsSubmitting(true);
    try {
      const response = await registerAccount(payload);
      return await applyAuthResponse(response);
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshAccount = async () => {
    if (!token) return null;
    const response = await fetchCurrentUser(token);
    setUser(response.user || null);
    writeStoredSession({ accessToken: token, user: response.user || null });
    return response.user || null;
  };

  const refreshSessions = async () => {
    if (!token) {
      setSessions([]);
      return [];
    }
    const response = await fetchSessions(token);
    setSessions(response.sessions || []);
    return response.sessions || [];
  };

  const revokeOtherSession = async (sessionId) => {
    if (!token) return null;
    const response = await revokeSession(token, sessionId);
    setSessions((current) => current.filter((session) => session.id !== response?.revokedSessionId));
    return response?.revokedSessionId || null;
  };

  const signOut = async () => {
    const activeToken = token || readStoredSession()?.accessToken;

    try {
      if (activeToken) {
        await logoutAccount(activeToken);
      }
    } catch {
      // Локально очищаем сессию даже если токен уже недействителен на сервере.
    } finally {
      clearStoredSession();
      setToken(null);
      setUser(null);
      setSessions([]);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        sessions,
        isLoading,
        isSubmitting,
        isAuthenticated: Boolean(token && user),
        signIn,
        signUp,
        signOut,
        refreshAccount,
        refreshSessions,
        revokeOtherSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
